/* Linsoul product hero: gallery, native variant selection and AJAX add to cart (Minimog cart drawer). */
(() => {
  'use strict';

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const pad = (n) => String(n).padStart(2, '0');

  const eager = (img) => {
    if (img && img.loading !== 'eager') img.loading = 'eager';
  };
  const idle = (fn) => ('requestIdleCallback' in window ? window.requestIdleCallback(fn, { timeout: 2500 }) : window.setTimeout(fn, 800));

  function initGallery(root) {
    const track = root.querySelector('[data-lsp-track]');
    if (!track) return { showMedia() {} };
    const stage = track.closest('.lsp-stage');
    const strip = root.querySelector('[data-lsp-thumbs]');
    const slides = [...track.querySelectorAll('[data-lsp-slide]')];
    const thumbs = [...root.querySelectorAll('[data-lsp-thumb]')];
    const count = root.querySelector('[data-lsp-count]');
    const prev = root.querySelector('[data-lsp-prev]');
    const next = root.querySelector('[data-lsp-next]');
    const last = slides.length - 1;
    let index = 0;

    // Off-screen slides are lazy; start fetching the ones a click or swipe can reach next.
    const warm = (from, to = from) => {
      for (let n = Math.max(0, from); n <= Math.min(last, to); n += 1) eager(slides[n].querySelector('img'));
    };
    const warmThumbs = (i) => thumbs.slice(Math.max(0, i - 6), i + 10).forEach((t) => eager(t.querySelector('img')));

    slides.forEach((slide) => {
      const img = slide.querySelector('img');
      if (!img || (img.complete && img.naturalWidth)) return;
      slide.classList.add('is-loading');
      const done = () => slide.classList.remove('is-loading');
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    });

    let syncNav = () => {};
    const setActive = (i) => {
      index = i;
      thumbs.forEach((t, n) => (n === i ? t.setAttribute('aria-current', 'true') : t.removeAttribute('aria-current')));
      if (count) count.textContent = pad(i + 1) + ' / ' + pad(slides.length);
      if (prev) prev.disabled = i === 0;
      if (next) next.disabled = i === last;
      warm(i - 1, i + 2);
      warmThumbs(i);
      syncNav(i);
      slides.forEach((s, n) => {
        if (n !== i) s.querySelectorAll('video').forEach((v) => v.pause());
      });
    };

    let show;
    const Flickity = window.Flickity;
    if (Flickity && slides.length > 1) {
      const fast = reduceMotion();
      const main = new Flickity(track, {
        cellSelector: '[data-lsp-slide]',
        setGallerySize: false,
        cellAlign: 'left',
        contain: true,
        pageDots: false,
        prevNextButtons: false,
        dragThreshold: 8,
        selectedAttraction: fast ? 1 : 0.09,
        friction: fast ? 1 : 0.62,
      });
      // Focus inside a clipped Flickity viewport scrolls it natively and misaligns the cells.
      const pin = (f) => f.viewport.addEventListener('scroll', () => {
        if (f.viewport.scrollLeft) f.viewport.scrollLeft = 0;
      }, { passive: true });
      show = (i, instant) => main.select(Math.max(0, Math.min(last, i)), false, instant || fast);
      main.on('select', (n) => setActive(n));
      pin(main);

      if (strip && thumbs.length) {
        const nav = new Flickity(strip, {
          cellSelector: '[data-lsp-thumb]',
          cellAlign: 'center',
          contain: true,
          freeScroll: true,
          freeScrollFriction: 0.08,
          pageDots: false,
          prevNextButtons: false,
          accessibility: false,
          dragThreshold: 6,
          selectedAttraction: fast ? 1 : 0.09,
          friction: fast ? 1 : 0.62,
        });
        const edges = () => {
          const view = nav.viewport.getBoundingClientRect();
          const first = thumbs[0].getBoundingClientRect();
          const end = thumbs[thumbs.length - 1].getBoundingClientRect();
          strip.toggleAttribute('data-fade-start', first.left < view.left - 1);
          strip.toggleAttribute('data-fade-end', end.right > view.right + 1);
        };
        syncNav = (i) => nav.selectCell(i, false, fast);
        nav.on('scroll', edges);
        pin(nav);
        nav.on('settle', () => warmThumbs(nav.selectedIndex));
        nav.on('staticClick', (event, pointer, cell, n) => {
          if (typeof n === 'number') show(n);
        });
        thumbs.forEach((t, n) => {
          t.addEventListener('click', (e) => {
            if (e.detail === 0) show(n, true);
          });
          t.addEventListener('focus', () => nav.selectCell(n, false, true));
          t.addEventListener('pointerenter', () => warm(n - 1, n + 1));
          t.addEventListener('pointerdown', () => warm(n), { passive: true });
        });
        const fit = () => {
          const overflow = nav.slideableWidth > nav.size.width + 1;
          if (nav.options.draggable !== overflow) {
            nav.options.draggable = overflow;
            nav.updateDraggable();
          }
          edges();
        };
        fit();
        if ('ResizeObserver' in window) new ResizeObserver(() => { nav.resize(); fit(); }).observe(strip);
      }
      if ('ResizeObserver' in window && stage) new ResizeObserver(() => main.resize()).observe(stage);
    } else {
      show = (i, instant) => {
        const target = Math.max(0, Math.min(last, i));
        track.scrollTo({ left: slides[target].offsetLeft, behavior: instant || reduceMotion() ? 'auto' : 'smooth' });
        setActive(target);
      };
      let raf = 0;
      track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
          if (i !== index) setActive(i);
        });
      }, { passive: true });
      thumbs.forEach((t, n) => t.addEventListener('click', () => show(n)));
    }

    prev?.addEventListener('click', () => show(index - 1));
    next?.addEventListener('click', () => show(index + 1));
    prev?.addEventListener('pointerenter', () => warm(index - 2, index - 1));
    next?.addEventListener('pointerenter', () => warm(index + 1, index + 2));
    track.addEventListener('keydown', (e) => {
      const map = { Home: 0, End: last };
      if (!track.classList.contains('flickity-enabled')) Object.assign(map, { ArrowLeft: index - 1, ArrowRight: index + 1 });
      if (e.key in map) {
        e.preventDefault();
        show(map[e.key], true);
      }
    });
    setActive(0);
    idle(() => warm(1, 3));

    const lightbox = root.querySelector('[data-lsp-lightbox]');
    const zoom = root.querySelector('[data-lsp-zoom]');
    if (lightbox && zoom) {
      const images = slides.map((s, n) => ({ n, img: s.querySelector('img[data-lsp-zoom-src]') })).filter((x) => x.img);
      const lbImg = lightbox.querySelector('[data-lsp-lightbox-img]');
      const caption = lightbox.querySelector('[data-lsp-lightbox-caption]');
      const figure = lightbox.querySelector('[data-lsp-lb-figure]');
      const cache = new Map();
      const load = (src) => {
        if (!cache.has(src)) {
          const pre = new Image();
          pre.decoding = 'async';
          pre.src = src;
          cache.set(src, (pre.decode ? pre.decode() : new Promise((ok, fail) => { pre.onload = ok; pre.onerror = fail; })).catch(() => {}));
        }
        return cache.get(src);
      };
      let lbIndex = 0;
      let token = 0;
      let spinTimer = 0;
      const render = (i) => {
        lbIndex = (i + images.length) % images.length;
        const { img } = images[lbIndex];
        const src = img.dataset.lspZoomSrc;
        const mine = ++token;
        caption.textContent = img.alt + ' / ' + pad(lbIndex + 1) + ' / ' + pad(images.length);
        figure?.setAttribute('aria-busy', 'true');
        figure?.classList.add('is-pending');
        clearTimeout(spinTimer);
        spinTimer = setTimeout(() => { if (mine === token) figure?.classList.add('is-loading'); }, 120);
        load(src).then(() => {
          if (mine !== token) return;
          clearTimeout(spinTimer);
          lbImg.src = src;
          lbImg.alt = img.alt;
          figure?.classList.remove('is-pending', 'is-loading');
          figure?.removeAttribute('aria-busy');
          if (images.length > 1) {
            [1, -1].forEach((step) => load(images[(lbIndex + step + images.length) % images.length].img.dataset.lspZoomSrc));
          }
        });
      };
      zoom.addEventListener('click', () => {
        const current = images.findIndex((x) => x.n === index);
        render(current < 0 ? 0 : current);
        lightbox.showModal();
      });
      lightbox.querySelector('[data-lsp-close]').addEventListener('click', () => lightbox.close());
      lightbox.querySelector('[data-lsp-lb-prev]')?.addEventListener('click', () => render(lbIndex - 1));
      lightbox.querySelector('[data-lsp-lb-next]')?.addEventListener('click', () => render(lbIndex + 1));
      lightbox.addEventListener('click', (e) => { if (e.target === lightbox) lightbox.close(); });
      lightbox.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') render(lbIndex - 1);
        if (e.key === 'ArrowRight') render(lbIndex + 1);
      });
      lightbox.addEventListener('close', () => {
        show(images[lbIndex].n, true);
        zoom.focus();
      });
    }

    return {
      showMedia(mediaId) {
        const i = slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
        if (i >= 0) show(i);
      },
    };
  }

  function initProduct(root) {
    let data;
    try {
      data = JSON.parse(root.querySelector('[data-lsp-product]').textContent);
    } catch (error) {
      console.warn('Linsoul product data unreadable', error);
      return;
    }
    const gallery = initGallery(root);
    const buy = root.querySelector('[data-lsp-buy]');
    const form = root.querySelector('[data-lsp-form]');
    const idInput = root.querySelector('[data-lsp-variant-id]');
    const submit = root.querySelector('[data-lsp-submit]');
    const submitLabel = root.querySelector('[data-lsp-submit-label]');
    const errorBox = root.querySelector('[data-lsp-error]');
    const dynamic = root.querySelector('[data-lsp-dynamic]');
    const qty = root.querySelector('[data-lsp-qty]');
    const priceNow = root.querySelector('[data-lsp-price-now]');
    const priceWas = root.querySelector('[data-lsp-price-was]');
    const priceSave = root.querySelector('[data-lsp-price-save]');
    const labels = buy ? buy.dataset : {};
    let current = data.variants.find((v) => String(v.id) === idInput?.value) || data.variants[0];
    let busy = false;

    let errorMessage = '';
    const broadcast = () => {
      root.dispatchEvent(new CustomEvent('linsoul:product-state', {
        bubbles: true,
        detail: {
          handle: root.dataset.productHandle,
          variant: current,
          quantity: qty ? Number(qty.value) || 1 : 1,
          busy,
          label: submitLabel ? submitLabel.textContent : '',
          disabled: submit ? submit.disabled : true,
          error: errorMessage,
        },
      }));
    };

    const showError = (message) => {
      errorMessage = message || '';
      if (errorBox) {
        errorBox.textContent = errorMessage;
        errorBox.hidden = !errorMessage;
      }
      broadcast();
    };

    const setButton = () => {
      if (!submit) return;
      const qtyOk = !qty || validQty();
      let label = labels.labelAdd;
      if (!current) label = labels.labelUnavailable;
      else if (!current.available) label = labels.labelSoldOut;
      if (busy) label = labels.labelAdding;
      submitLabel.textContent = label;
      submit.disabled = !current || !current.available || busy || !qtyOk;
      submit.setAttribute('aria-busy', String(busy));
      if (dynamic) dynamic.hidden = !current || !current.available;
      broadcast();
    };
    root.lspRequestState = broadcast;

    function validQty() {
      const value = Number(qty.value);
      const ok = qty.value.trim() !== '' && Number.isInteger(value) && value >= 1 && value <= Number(qty.max || 999);
      qty.setAttribute('aria-invalid', String(!ok));
      root.querySelector('[data-lsp-step="-1"]').disabled = ok && value <= 1;
      root.querySelector('[data-lsp-step="1"]').disabled = ok && value >= Number(qty.max || 999);
      return ok;
    }

    if (qty) {
      root.querySelectorAll('[data-lsp-step]').forEach((b) => b.addEventListener('click', () => {
        const n = Number.isInteger(Number(qty.value)) ? Number(qty.value) : 1;
        qty.value = String(Math.max(1, Math.min(Number(qty.max || 999), n + Number(b.dataset.lspStep))));
        showError('');
        setButton();
      }));
      qty.addEventListener('input', () => {
        showError(validQty() ? '' : labels.labelQtyError);
        setButton();
      });
    }

    // Options → variant
    const optionInputs = [...root.querySelectorAll('[data-lsp-option]')];
    const optionCount = optionInputs.length ? Math.max(...optionInputs.map((i) => Number(i.dataset.lspOption))) + 1 : 0;
    const selected = () => {
      const values = [];
      for (let i = 0; i < optionCount; i += 1) {
        values[i] = root.querySelector(`[data-lsp-option="${i}"]:checked`)?.value;
      }
      return values;
    };
    // Keep existing variants visible even when sold out. Later options still depend on the
    // selected prefix, so combinations that do not exist stay hidden (e.g. complex cable options).
    // A sold-out selection stays selected; the purchase controls below already prevent buying it.
    const syncOptions = () => {
      const picked = [];
      for (let i = 0; i < optionCount; i += 1) {
        const inputs = optionInputs.filter((input) => Number(input.dataset.lspOption) === i);
        let first = null;
        let firstAvailable = null;
        let keep = false;
        inputs.forEach((input) => {
          const matches = data.variants.filter((v) => v.options[i] === input.value
            && picked.every((p, n) => v.options[n] === p));
          const exists = matches.length > 0;
          const available = matches.some((v) => v.available);
          const chip = input.closest('.lsp-chip');
          const note = chip.querySelector('[data-lsp-unavailable]');
          chip.hidden = !exists;
          input.disabled = !exists;
          chip.toggleAttribute('data-unavailable', exists && !available);
          note.textContent = labels.labelSoldOut || 'Sold out';
          note.hidden = !exists || available;
          if (!exists) return;
          first = first || input;
          if (available) firstAvailable = firstAvailable || input;
          if (input.checked) keep = true;
        });
        const fallback = firstAvailable || first;
        if (!keep && fallback) fallback.checked = true;
        picked[i] = (keep ? inputs.find((input) => input.checked) : fallback)?.value;
      }
    };
    const updateVariant = (fromUser) => {
      syncOptions();
      const values = selected();
      current = data.variants.find((v) => v.options.every((o, n) => o === values[n])) || null;
      values.forEach((value, i) => {
        const out = root.querySelector(`[data-lsp-option-value="${i}"]`);
        if (out) out.textContent = value || '';
      });
      if (current) {
        if (idInput) idInput.value = current.id;
        if (priceNow) priceNow.innerHTML = current.price;
        if (priceWas) {
          priceWas.innerHTML = current.compare || '';
          priceWas.hidden = !current.compare;
        }
        if (priceSave) {
          const esc = (priceSave.dataset.template || '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
          priceSave.innerHTML = current.save ? esc.replace('[amount]', current.save) : '';
          priceSave.hidden = !current.save;
        }
        if (fromUser && current.media) gallery.showMedia(current.media);
        if (fromUser && !root.hasAttribute('data-lsp-no-history')) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', current.id);
          window.history.replaceState(window.history.state, '', url.toString());
        }
      }
      showError('');
      setButton();
      root.dispatchEvent(new CustomEvent('linsoul:product-context', { bubbles: true, detail: { variant: current, handle: root.dataset.productHandle } }));
    };
    optionInputs.forEach((input) => input.addEventListener('change', () => updateVariant(true)));
    if (optionInputs.length) updateVariant(false);
    else setButton();

    // Add to cart
    form?.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (busy) return;
      if (qty && !validQty()) {
        showError(labels.labelQtyError);
        qty.focus();
        return;
      }
      if (!current || !current.available) return;
      busy = true;
      showError('');
      setButton();

      const drawer = document.querySelector('m-cart-drawer');
      const useDrawer = drawer && window.MinimogSettings?.enable_cart_drawer && typeof drawer.renderContents === 'function';
      const body = new FormData(form);
      if (useDrawer) {
        body.append('sections', drawer.getSectionsToRender().map((s) => s.id));
        body.append('sections_url', window.location.pathname);
      }
      try {
        const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body,
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          showError(result.description || result.message || labels.labelError);
          return;
        }
        document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: result } }));
        if (useDrawer) {
          drawer.renderContents(result);
          drawer.getCart?.().then((cart) => cart && drawer.updateCartCount(cart.item_count));
        } else {
          window.location.href = window.Shopify?.routes?.root ? window.Shopify.routes.root + 'cart' : '/cart';
        }
      } catch (error) {
        console.warn(error);
        showError(labels.labelError);
      } finally {
        busy = false;
        setButton();
      }
    });
  }

  // Globo Product Options leaves its app block empty and injects its options (a gpo-container) into the add-to-cart form
  // (its inputs must be inside it), so block order can't put the pre-order card under the options. Move it there once Globo lands.
  function placePreorder(root) {
    const card = root.querySelector('.lsp-preorder');
    const form = root.querySelector('[data-lsp-form]');
    if (!card || !form) return;
    const place = () => {
      const app = [...form.children].find((el) => el.matches('gpo-container, .gpo-app'));
      if (!app) return false;
      if (app.nextElementSibling !== card) app.after(card);
      return true;
    };
    if (place()) return;
    const observer = new MutationObserver(() => { if (place()) observer.disconnect(); });
    observer.observe(form, { childList: true });
    setTimeout(() => observer.disconnect(), 20000);
  }

  const init = () => document.querySelectorAll('[data-lsp-hero]').forEach((root) => {
    if (root.dataset.lspReady) return;
    root.dataset.lspReady = '1';
    initProduct(root);
    placePreorder(root);
  });

  window.LsProduct = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
